// 档2 — Volcengine ASR microphone playground (flagship WS Try-It).
//
// Flow: getUserMedia → AudioContext capture → downsample to 16k/16-bit/mono PCM → frame via
// VolcAsrClient (binary protocol) → WS gateway → live transcription from decoded downstream
// frames. Duration + estimated cost read from wsBilling.perMinuteUsd (and the upstream
// audio_info.duration when present, which is the authoritative billed figure).
//
// Graceful degradation: no-mic / permission-denied / non-secure-context / no-gzip all show a
// clear inline message and never crash. Falls back to the generic console conceptually — the
// parent decides which panel to render; here we just refuse to start capture.

import React from 'react';
import { XPrim, XIcons } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import { VolcAsrClient } from './ws-adapter-client.js';
import { gzipSupported } from './gzip.js';

const FRAME_MS = 200; // matches demo-asr.mjs pacing

// Fallback config schema — used only when GET /ws/adapters hasn't populated userParams /
// audioParams yet (older backend). The backend catalog is the source of truth when present,
// so this stays a thin safety net rather than a second source of truth.
const FALLBACK_AUDIO = { rates: [16000, 8000], defaultRate: 16000, bits: 16, channels: 1 };
const FALLBACK_USER_PARAMS = [
  { key: 'enable_punc', label: '标点符号', type: 'bool', default: true },
  { key: 'enable_itn', label: '数字归一化 (ITN)', type: 'bool', default: true },
  { key: 'enable_ddc', label: '语义顺滑', type: 'bool', default: false },
  { key: 'show_utterances', label: '返回分句与时间戳', type: 'bool', default: false },
  {
    key: 'result_type', label: '结果返回方式', type: 'enum', default: 'full',
    options: [{ value: 'full', label: '全量累积' }, { value: 'single', label: '增量' }],
  },
];

// Build the initial value map from a userParams schema (key → default).
const initParamValues = (params) =>
  (params || []).reduce((acc, p) => { acc[p.key] = p.default; return acc; }, {});

// Downsample Float32 @ inputRate → Int16 @ targetRate (linear decimation). Returns Uint8Array.
// targetRate MUST equal the rate declared in the config frame, or the upstream gets garbage.
function floatToPcm16(float32, inputRate, targetRate) {
  const ratio = inputRate / targetRate;
  const outLen = Math.floor(float32.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const s = Math.max(-1, Math.min(1, float32[Math.floor(i * ratio)]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return new Uint8Array(out.buffer);
}

const fmtDuration = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

const isSecure = () =>
  typeof window !== 'undefined' &&
  (window.isSecureContext || /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname));

export default function WsPlaygroundAsr({ api, endpoint, adapterMeta }) {
  const wsConfig = (endpoint && endpoint._raw && endpoint._raw.wsConfig) || {};
  const wsBilling = (endpoint && endpoint._raw && endpoint._raw.wsBilling) || {};
  const clientPath = wsConfig.clientPath || '/v1/asr';
  const perMinuteUsd = Number(wsBilling.perMinuteUsd) || 0;

  // Config surface is adapter-driven: prefer the backend catalog (GET /ws/adapters), fall
  // back to a built-in schema only if this backend predates userParams/audioParams.
  const userParams = (adapterMeta && adapterMeta.userParams) || FALLBACK_USER_PARAMS;
  const audioParams = (adapterMeta && adapterMeta.audioParams) || FALLBACK_AUDIO;
  const rates = (audioParams.rates && audioParams.rates.length ? audioParams.rates : FALLBACK_AUDIO.rates);

  const [state, setState] = React.useState('idle'); // idle | connecting | recording | stopping | closed | error
  const [transcript, setTranscript] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState('');
  const [elapsedMs, setElapsedMs] = React.useState(0);
  const [billedMs, setBilledMs] = React.useState(0); // authoritative duration from upstream audio_info
  const [showConfig, setShowConfig] = React.useState(false);
  const [sampleRate, setSampleRate] = React.useState(audioParams.defaultRate || 16000);
  const [cfgValues, setCfgValues] = React.useState(() => initParamValues(userParams));

  const wsRef = React.useRef(null);
  const clientRef = React.useRef(null);
  const audioCtxRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const nodeRef = React.useRef(null);
  const seqRef = React.useRef(1);
  const startRef = React.useRef(0);
  const tickRef = React.useRef(null);
  const pcmBufRef = React.useRef([]); // queued Uint8Array chunks awaiting a 200ms frame
  const bytesPerFrameRef = React.useRef((audioParams.defaultRate || 16000) * 2 * (FRAME_MS / 1000));
  const resultTypeRef = React.useRef('full'); // snapshot at start(): 'full' replace / 'single' append

  // Full teardown — used by stop() and on unmount. Closes mic tracks, audio graph, socket,
  // and clears the timer so nothing leaks after the panel goes away.
  const teardown = React.useCallback((closeSocket = true) => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (nodeRef.current) { try { nodeRef.current.disconnect(); } catch {} nodeRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
    if (streamRef.current) { try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch {} streamRef.current = null; }
    if (closeSocket && wsRef.current) { try { wsRef.current.close(1000, 'client stop'); } catch {} wsRef.current = null; }
    pcmBufRef.current = [];
  }, []);

  React.useEffect(() => () => teardown(true), [teardown]);

  const preflightError = React.useMemo(() => {
    if (!isSecure()) return 'Microphone requires HTTPS (or localhost). Open this page over a secure connection.';
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return 'This browser does not expose microphone access (getUserMedia unavailable).';
    }
    if (!gzipSupported()) return 'This browser lacks the Compression Streams API required for the Volcengine binary protocol.';
    return '';
  }, []);

  const flushFrame = React.useCallback(async (last = false) => {
    const ws = wsRef.current;
    const client = clientRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !client) return;
    // Coalesce queued chunks into one payload.
    const chunks = pcmBufRef.current;
    let total = 0;
    for (const c of chunks) total += c.length;
    if (total === 0 && !last) return;
    const merged = new Uint8Array(total);
    let off = 0;
    for (const c of chunks) { merged.set(c, off); off += c.length; }
    pcmBufRef.current = [];
    seqRef.current += 1;
    try {
      const frame = await client.encodeAudio(merged, { seq: seqRef.current, last });
      if (ws.readyState === WebSocket.OPEN) ws.send(frame);
    } catch (e) {
      setErrorMsg('Encode/send failed: ' + (e && e.message ? e.message : e));
    }
  }, []);

  const start = async () => {
    setErrorMsg('');
    setTranscript('');
    setBilledMs(0);
    if (preflightError) { setErrorMsg(preflightError); setState('error'); return; }
    setState('connecting');

    // Snapshot config at start — the config controls are disabled while a session is active,
    // so `rate`/`request` stay stable for the whole connection (config frame ↔ resampler must
    // agree on the sample rate).
    const rate = sampleRate;
    const request = { ...cfgValues };
    resultTypeRef.current = cfgValues.result_type === 'single' ? 'single' : 'full';
    bytesPerFrameRef.current = rate * 2 * (FRAME_MS / 1000);

    // 1) mic first — so a permission denial fails fast before we mint a token.
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
    } catch (e) {
      setState('error');
      setErrorMsg(
        e && e.name === 'NotAllowedError' ? 'Microphone permission denied.'
          : e && e.name === 'NotFoundError' ? 'No microphone found on this device.'
          : 'Could not access microphone: ' + (e && e.message ? e.message : e),
      );
      return;
    }
    streamRef.current = stream;

    // 2) mint ws-token
    let token;
    try {
      const minted = await XApi.keyService.getWsToken(endpoint._raw?.id || endpoint.id);
      token = minted && minted.token;
      if (!token) throw new Error('mint returned no token');
    } catch (e) {
      teardown(false);
      setState('error');
      setErrorMsg('ws-token mint failed: ' + (e && e.message ? e.message : e));
      return;
    }

    // 3) connect
    const url = XApi.helpers.buildWsGatewayUrl(api.host, clientPath, { endpoint: endpoint._raw?.id || endpoint.id });
    const client = new VolcAsrClient();
    clientRef.current = client;
    let ws;
    try { ws = new WebSocket(url, XApi.helpers.buildWsCredentialProtocols(token)); } catch (e) {
      teardown(false);
      setState('error');
      setErrorMsg('WebSocket failed: ' + (e && e.message ? e.message : e));
      return;
    }
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    seqRef.current = 1;

    ws.onopen = async () => {
      setState('recording');
      startRef.current = Date.now();
      setElapsedMs(0);
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = setInterval(() => setElapsedMs(Date.now() - startRef.current), 250);
      try {
        const cfg = await client.buildConfigFrame({ sampleRate: rate, request });
        if (ws.readyState === WebSocket.OPEN) ws.send(cfg);
      } catch (e) {
        setErrorMsg('Config frame failed: ' + (e && e.message ? e.message : e));
      }
      startCapture(stream, rate);
    };
    ws.onmessage = async (ev) => {
      if (typeof ev.data === 'string') return; // upstream is binary
      try {
        const r = await client.decodeResult(ev.data);
        if (r.error) { setErrorMsg(`Upstream error ${r.error.code}: ${r.error.message}`); return; }
        // result_type='full' (default): each frame carries the cumulative text → replace.
        // 'single': each frame is only the new segment → append to the running transcript.
        if (r.text) {
          if (resultTypeRef.current === 'single') setTranscript((prev) => prev + r.text);
          else setTranscript(r.text);
        }
        if (r.durationMs) setBilledMs(r.durationMs);
      } catch { /* ignore malformed frame */ }
    };
    ws.onerror = () => setErrorMsg((m) => m || 'WebSocket error');
    ws.onclose = (ev) => {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      teardown(false);
      wsRef.current = null;
      setState((s) => (s === 'error' ? s : 'closed'));
      if (ev.code !== 1000 && ev.code !== 1005) setErrorMsg((m) => m || `Closed · code ${ev.code}${ev.reason ? ` · ${ev.reason}` : ''}`);
    };
  };

  const startCapture = (stream, targetRate) => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    // ScriptProcessorNode is deprecated but universally available and simplest for a Try-It.
    // 4096-frame buffer at the device rate; we downsample to targetRate and buffer to 200ms frames.
    const proc = ctx.createScriptProcessor(4096, 1, 1);
    nodeRef.current = proc;
    proc.onaudioprocess = (e) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);
      const pcm = floatToPcm16(input, ctx.sampleRate, targetRate);
      pcmBufRef.current.push(pcm);
      let queued = 0;
      for (const c of pcmBufRef.current) queued += c.length;
      if (queued >= bytesPerFrameRef.current) flushFrame(false);
    };
    source.connect(proc);
    proc.connect(ctx.destination);
  };

  const stop = async () => {
    if (state !== 'recording') { teardown(true); setState('closed'); return; }
    setState('stopping');
    // Stop capturing, flush a final (last=true) frame so upstream finalizes, then close.
    if (nodeRef.current) { try { nodeRef.current.disconnect(); } catch {} nodeRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
    if (streamRef.current) { try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch {} streamRef.current = null; }
    await flushFrame(true);
    // Give the upstream a moment to emit the final transcript before we close.
    setTimeout(() => { if (wsRef.current) { try { wsRef.current.close(1000, 'client stop'); } catch {} } }, 1200);
  };

  const recording = state === 'recording';
  const connecting = state === 'connecting';
  const stopping = state === 'stopping';
  const effectiveMs = billedMs > 0 ? billedMs : elapsedMs;
  const estCost = perMinuteUsd > 0 ? (effectiveMs / 60000) * perMinuteUsd : 0;

  // Config is locked while a session is live (frame ↔ resampler must not change mid-stream).
  const configLocked = connecting || recording || stopping;
  const setParam = (key, value) => setCfgValues((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="col gap-3" style={{ minWidth: 0 }}>
      {preflightError && state === 'idle' && (
        <div className="row gap-2" style={{ padding: '10px 12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, fontSize: 12.5, color: 'var(--warn)' }}>
          <XIcons.IconInfo size={13} /> <span>{preflightError}</span>
        </div>
      )}

      {/* Config panel — adapter-driven: sample rate + userParams from GET /ws/adapters.
          Collapsed by default; sensible defaults mean users can just hit Start. */}
      <div className="card">
        <button
          onClick={() => setShowConfig((v) => !v)}
          style={{
            width: '100%', padding: '10px 14px', background: 'transparent', border: 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: showConfig ? '1px solid var(--line)' : 'none', color: 'var(--fg-2)',
          }}>
          <span className="micro row gap-2" style={{ alignItems: 'center' }}>
            <XIcons.IconSliders size={12} /> 识别配置 · Recognition config
          </span>
          <XIcons.IconChevronDown size={13} style={{ transform: showConfig ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
        {showConfig && (
          <div className="col gap-3" style={{ padding: 14 }}>
            {/* Sample rate — the one audio knob users may pick; drives config frame AND resampler. */}
            <label className="col gap-1">
              <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>采样率 (Hz) · sample rate</span>
              <select className="input" disabled={configLocked} value={sampleRate}
                onChange={(e) => setSampleRate(Number(e.target.value))} style={{ maxWidth: 220 }}>
                {rates.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>

            {/* Recognition params (透传 → 上游 request) */}
            {userParams.map((p) => {
              if (p.type === 'bool') {
                return (
                  <label key={p.key} className="row gap-2" style={{ alignItems: 'baseline', cursor: configLocked ? 'default' : 'pointer' }}>
                    <input type="checkbox" disabled={configLocked} checked={!!cfgValues[p.key]}
                      onChange={(e) => setParam(p.key, e.target.checked)} style={{ marginTop: 2 }} />
                    <span style={{ fontSize: 13, color: 'var(--fg-1)' }}>{p.label}</span>
                    {p.hint && <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>· {p.hint}</span>}
                  </label>
                );
              }
              if (p.type === 'enum') {
                return (
                  <label key={p.key} className="col gap-1">
                    <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>{p.label}{p.hint ? ` · ${p.hint}` : ''}</span>
                    <select className="input" disabled={configLocked} value={cfgValues[p.key]}
                      onChange={(e) => setParam(p.key, e.target.value)} style={{ maxWidth: 320 }}>
                      {(p.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                );
              }
              return null;
            })}

            {configLocked && (
              <div style={{ fontSize: 11, color: 'var(--fg-4)' }}>会话进行中，配置已锁定；停止后可修改。</div>
            )}
          </div>
        )}
      </div>

      {/* Mic control */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">Live transcription · Volcengine ASR</div>
          <div className="row gap-2" style={{ alignItems: 'center', fontSize: 12 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: recording ? 'var(--err)' : connecting ? 'var(--c-amber)' : 'var(--fg-4)',
              boxShadow: recording ? '0 0 0 4px rgba(231,76,60,0.15)' : 'none',
            }} />
            <span style={{ color: recording ? 'var(--err)' : 'var(--fg-3)' }}>
              {recording ? 'Recording' : connecting ? 'Connecting…' : stopping ? 'Finishing…' : state === 'closed' ? 'Stopped' : 'Idle'}
            </span>
          </div>
        </div>
        <div style={{ padding: 18, textAlign: 'center' }}>
          {!recording ? (
            <button className="btn btn-primary" onClick={start} disabled={connecting || stopping || !!preflightError}
              style={{ height: 44, minWidth: 200, justifyContent: 'center', fontSize: 14 }}>
              {connecting ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Connecting…</> : <><XIcons.IconPlay size={14} /> Start speaking</>}
            </button>
          ) : (
            <button className="btn" onClick={stop} disabled={stopping}
              style={{ height: 44, minWidth: 200, justifyContent: 'center', fontSize: 14, borderColor: 'var(--err)', color: 'var(--err)' }}>
              <XIcons.IconPause size={14} /> Stop
            </button>
          )}
          <div className="row gap-3" style={{ justifyContent: 'center', marginTop: 14, fontSize: 12.5, color: 'var(--fg-3)', flexWrap: 'wrap' }}>
            <span>Duration <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtDuration(effectiveMs)}</span></span>
            {perMinuteUsd > 0 && (
              <span>Est. cost <span className="mono" style={{ color: 'var(--fg-1)' }}>${estCost.toFixed(6)}</span> <span style={{ color: 'var(--fg-4)' }}>(${perMinuteUsd}/min)</span></span>
            )}
          </div>
        </div>
      </div>

      {errorMsg && (
        <div className="row gap-2" style={{ padding: '10px 12px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 8, fontSize: 12.5, color: 'var(--err)' }}>
          <XIcons.IconInfo size={13} /> <span style={{ overflowWrap: 'anywhere' }}>{errorMsg}</span>
        </div>
      )}

      {/* Transcript */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">Transcript</div>
          {transcript && <XPrim.CopyBtn text={transcript} />}
        </div>
        <div style={{ padding: 18, minHeight: 120, fontSize: 15, lineHeight: 1.7, color: transcript ? 'var(--fg-1)' : 'var(--fg-4)' }}>
          {transcript || (recording ? 'Listening… speak into your microphone.' : 'Press “Start speaking” and talk. Live results appear here.')}
        </div>
      </div>
    </div>
  );
}
